import { useCallback, useEffect, useState } from "react";
import { SECTION_IDS, type SectionKey } from "./copy";

/**
 * What the header needs to know about the page under it: how far down we are,
 * and which section the reader is actually in.
 *
 * 🔴 THE NAV MUST NOT WRITE TO `window.location.hash`, AND THAT IS NOT A STYLE
 * PREFERENCE. `useRoute` listens for `hashchange` and runs `matchRoute` on the
 * result, so the old `href="#how-it-works"` links fired the router on every nav
 * click, failed to match any route, and pushed a junk entry into the back stack
 * — four clicks meant four presses of Back to leave the page. `scrollTo` with
 * the default prevented does the same job and the router never hears about it.
 *
 * 🔴 THE ACTIVE TAB IS DERIVED FROM SCROLL POSITION, NOT FROM THE LAST CLICK.
 * The previous header held `useState<'home' | 'how' | …>` set only in `onClick`,
 * so a reader who scrolled — which is how anyone reads a landing page — saw
 * "Home" highlighted at the bottom of the privacy section. A navigation
 * indicator that disagrees with the page is worse than no indicator.
 *
 * ⚠️ ONE `scroll` LISTENER, rAF-COALESCED, FOR BOTH ANSWERS. This product ships
 * to five-year-old school laptops; a listener that measured layout on every
 * scroll event would do it dozens of times per frame, and `getBoundingClientRect`
 * forces synchronous layout each time.
 */

/**
 * The sections, in the order they appear.
 *
 * ⚠️ DERIVED FROM `SECTION_IDS`, WHOSE KEY ORDER IS THE PAGE ORDER. A section
 * added to or removed from the page cannot leave a stale key here, and the
 * header draws its links from the same object — one list, read in three places.
 */
const ORDER = Object.keys(SECTION_IDS) as SectionKey[];

export interface SectionSpy {
  /** 0 at the top, 1 at the bottom. */
  progress: number;
  /** True past a few pixels — the header's cue to grow a background. */
  scrolled: boolean;
  /** `null` while the hero is still the thing being read. */
  active: SectionKey | null;
  /** Scrolls to a section and moves keyboard focus there. */
  goTo: (key: SectionKey) => void;
}

/**
 * A section the reader asked for from a page that does not contain it.
 *
 * 🔴 THE PRICING PAGE SHOWS THE LANDING HEADER, AND THAT HEADER OFFERS TWO
 * ANCHORS THAT ARE NOT ON IT. `goTo` is `getElementById` + `scrollIntoView`, so
 * on `#/connect` both "Games" and "For Schools" would find nothing and return
 * silently — a nav that looks live and does nothing, which is worse than a
 * missing link because the reader blames their own click. The header there
 * navigates home instead and leaves the key here; the landing page picks it up
 * on mount and finishes the scroll.
 *
 * ⚠️ MODULE STATE, NOT `sessionStorage` OR THE URL. Both pages live in one
 * document — the router only swaps `location.hash` — so a module variable
 * survives the transition and dies with the tab, which is exactly the lifetime
 * this needs. The URL is not an option for the reason this file already
 * documents at length: writing `#games` fires `hashchange`, fails `matchRoute`
 * and pushes junk into the back stack.
 *
 * ⚠️ IT IS CONSUMED, NOT READ. A key left in place would re-scroll the landing
 * page on every later mount — a reader who came back from `#/connect` a second
 * time would be thrown down the page without asking.
 */
let pendingSection: SectionKey | null = null;

export function requestSection(key: SectionKey): void {
  pendingSection = key;
}

/**
 * Go to a home-page section from a link that can sit on any page (the footer).
 *
 * If the section is on the page already, scroll to it and return `true` — the
 * caller then prevents the link's default, because writing `#/` over `#/` would
 * not navigate anywhere. Otherwise ask for it (`requestSection`) and return
 * `false`, so the link's `href="#/"` opens the home page and the spy scrolls on
 * arrival.
 */
export function goToSection(key: SectionKey): boolean {
  const el = document.getElementById(SECTION_IDS[key]);
  if (el) {
    el.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
    el.focus({ preventScroll: true });
    return true;
  }
  requestSection(key);
  return false;
}

/**
 * 🔴 PEEK AND CLEAR ARE SEPARATE, AND COLLAPSING THEM INTO ONE `consume()` IS A
 * BUG THAT ONLY APPEARS UNDER `StrictMode`. React double-invokes effects in
 * development: mount, clean up, mount again. A consume-on-read took the key on
 * the first mount, the cleanup cancelled its pending frame, and the second mount
 * read `null` — so the scroll never happened, silently, on every navigation.
 * Reading without taking makes the effect idempotent, which is exactly what
 * `StrictMode` is checking for.
 *
 * ⚠️ THE KEY IS CLEARED WHEN THE SCROLL FIRES OR WHEN THE WAIT GIVES UP — never
 * in a cleanup, because a cleanup here means "this mount is being discarded",
 * not "the reader no longer wants this section".
 */
export function peekPendingSection(): SectionKey | null {
  return pendingSection;
}

export function clearPendingSection(): void {
  pendingSection = null;
}

export function useSectionSpy(): SectionSpy {
  const [progress, setProgress] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState<SectionKey | null>(null);

  useEffect(() => {
    let frame = 0;

    const measure = (): void => {
      frame = 0;
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;

      setScrolled(y > 8);
      setProgress(max > 0 ? Math.min(1, Math.max(0, y / max)) : 0);

      /*
       * A section counts as "being read" once its top passes a line a third of
       * the way down the viewport. Reading the heading is what makes a section
       * the current one — waiting until it fills the screen would leave the
       * previous tab lit while its heading is off the top.
       */
      const line = y + window.innerHeight / 3;

      /*
       * ⚠️ THE BOTTOM OF THE PAGE IS A SPECIAL CASE. The last section is short
       * and the footer sits under it, so its top may never reach the line — and
       * the tab for it would never light. Landing within a pixel of the bottom
       * means the reader is in the last section, whatever the arithmetic says.
       */
      if (max > 0 && max - y < 2) {
        setActive(ORDER[ORDER.length - 1] ?? null);
        return;
      }

      let current: SectionKey | null = null;
      for (const key of ORDER) {
        const el = document.getElementById(SECTION_IDS[key]);
        if (!el) continue;
        if (el.offsetTop <= line) current = key;
      }
      setActive(current);
    };

    const onScroll = (): void => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    measure();

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const goTo = useCallback((key: SectionKey) => {
    const el = document.getElementById(SECTION_IDS[key]);
    if (!el) return;
    el.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "start",
    });
    /*
     * 🔴 SCROLLING IS NOT NAVIGATING FOR A KEYBOARD USER. Without moving focus,
     * the next Tab continues from the header link that was just activated, so
     * the reader is looking at the games section and tabbing through the nav.
     * `preventScroll` keeps `focus()` from fighting the smooth scroll it would
     * otherwise jump past. The sections carry `tabIndex={-1}` for this.
     */
    el.focus({ preventScroll: true });
  }, []);

  /*
   * ⚠️ `toTop` WENT WITH THE FOOTER CONTROL THAT CALLED IT. A back-to-top button
   * belongs on a page long enough to get lost in; this one is a hero, a row of
   * facts and two sections. Keeping the callback for a button that no longer
   * exists is how a hook accumulates surface nobody can explain later.
   */
  /*
   * Finish a scroll that started on another page.
   *
   * 🔴 IT WAITS FOR THE SECTION TO EXIST, AND THE FIRST VERSION DID NOT. One
   * `requestAnimationFrame` looked obviously sufficient — the effect runs before
   * layout, a frame later it is done — and it silently did nothing, every time.
   * This page is code-split: measured from a real click on `#/connect`, the
   * `#for-schools` node does not appear for about **420 ms** while its chunk
   * loads. A single frame lands at ~16 ms, `getElementById` returns null, and
   * `goTo` returns without a sound. That is the same silent-no-op failure this
   * whole handoff exists to prevent, reintroduced one layer down.
   *
   * ⚠️ SO IT POLLS BY FRAME, WITH A DEADLINE. Frames rather than an interval
   * because the answer is only ever needed at paint time; a deadline because a
   * loop that waits forever for a section that was renamed is a page that
   * scrolls itself half a minute after the reader has started reading. Two
   * seconds is comfortably past the measured 420 ms on this machine and short
   * enough that a genuine failure stays invisible rather than becoming a jump.
   *
   * ⚠️ THE GAME CARDS RESERVE THEIR HEIGHT (`aspect-4/3` on lazy images), so a
   * late-decoding picture cannot move the schools section out from under the
   * scroll after it lands.
   */
  useEffect(() => {
    const key = peekPendingSection();
    if (!key) return;

    let frame = 0;
    const deadline = Date.now() + 2000;

    const attempt = (): void => {
      if (document.getElementById(SECTION_IDS[key])) {
        clearPendingSection();
        goTo(key);
        return;
      }
      if (Date.now() > deadline) {
        clearPendingSection();
        return;
      }
      frame = window.requestAnimationFrame(attempt);
    };

    frame = window.requestAnimationFrame(attempt);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [goTo]);

  return { progress, scrolled, active, goTo };
}

/*
 * Smooth scrolling is motion, and the whole product honours the preference
 * (design-system.md §5). CSS cannot express `behavior: 'smooth'` conditionally
 * from here, so the check is explicit — and it is read at call time rather than
 * cached, because a reader can change the OS setting mid-session.
 *
 * ⚠️ The in-app Reduce Animation setting writes `data-reduce-motion` on the
 * root, so both switches are read, exactly as `marketing.css` treats them.
 */
export function prefersReducedMotion(): boolean {
  if (document.documentElement.dataset["reduceMotion"] === "true") return true;
  return typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
}
